import { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, PaintRoller, Wall, SquaresFour, Buildings, CheckCircle } from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const serviceIcons = {
  "platrerie-isolation": Wall,
  "peinture-decoration": PaintRoller,
  "revetements": SquaresFour,
  "ravalement-facades": Buildings,
};

export default function ServicesPage() {
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(true);
  const location = useLocation();

  useEffect(() => {
    const fetchServices = async () => {
      try {
        const response = await axios.get(`${API}/services`);
        setServices(response.data);
      } catch (error) {
        console.error("Error fetching services:", error);
      } finally {
        setLoading(false);
      }
    };
    fetchServices();
  }, []);

  useEffect(() => {
    if (location.hash) {
      const element = document.getElementById(location.hash.slice(1));
      if (element) {
        setTimeout(() => {
          element.scrollIntoView({ behavior: "smooth", block: "start" });
        }, 100);
      }
    }
  }, [location, services]);

  return (
    <div className="pt-20" data-testid="services-page">
      {/* Hero Section */}
      <section className="bg-[#F8F9FA] py-20" data-testid="services-hero">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-center max-w-3xl mx-auto"
          >
            <span className="text-[#1B93A4] font-medium uppercase tracking-wider text-sm">
              Nos Métiers
            </span>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-[#333333] mt-3">
              Des Services d'Excellence
            </h1>
            <p className="text-[#64748B] text-lg mt-6">
              Qualidéco vous accompagne dans tous vos projets de rénovation et décoration. 
              Découvrez notre gamme complète de prestations réalisées par des artisans qualifiés.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Services List */}
      <section className="py-24" data-testid="services-list">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="space-y-32">
            {services.map((service, index) => {
              const Icon = serviceIcons[service.id] || PaintRoller;
              const isEven = index % 2 === 0;

              return (
                <motion.div
                  key={service.id}
                  id={service.id}
                  initial={{ opacity: 0, y: 40 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, margin: "-100px" }}
                  transition={{ duration: 0.6 }}
                  className="scroll-mt-32"
                  data-testid={`service-section-${service.id}`}
                >
                  <div className={`grid lg:grid-cols-2 gap-12 items-center ${!isEven ? "lg:grid-flow-dense" : ""}`}>
                    {/* Image */}
                    <div className={`relative ${!isEven ? "lg:col-start-2" : ""}`}>
                      <div className="relative overflow-hidden rounded-sm shadow-2xl">
                        <img
                          src={service.image}
                          alt={service.title}
                          className="w-full h-[400px] object-cover"
                        />
                        <div className="absolute inset-0 bg-gradient-to-t from-black/40 to-transparent" />
                      </div>
                      {/* Floating Icon */}
                      <div className={`absolute -bottom-6 ${isEven ? "-right-6" : "-left-6"} bg-[#1B93A4] p-6 rounded-sm shadow-xl`}>
                        <Icon size={40} weight="fill" className="text-white" />
                      </div>
                    </div>

                    {/* Content */}
                    <div className={`space-y-6 ${!isEven ? "lg:col-start-1" : ""}`}>
                      <div className="inline-flex items-center gap-2 bg-[#F8F9FA] px-4 py-2 rounded-full">
                        <span className="w-2 h-2 bg-[#E74C3C] rounded-full" />
                        <span className="text-sm font-medium text-[#64748B]">Service #{index + 1}</span>
                      </div>

                      <h2 className="text-3xl sm:text-4xl font-bold text-[#333333]">
                        {service.title}
                      </h2>

                      <p className="text-[#64748B] text-lg leading-relaxed">
                        {service.description}
                      </p>

                      <div className="bg-[#F8F9FA] p-6 rounded-sm">
                        <h4 className="font-bold text-[#333333] mb-4">Nos prestations incluent :</h4>
                        <ul className="grid sm:grid-cols-2 gap-3">
                          {service.features.map((feature, i) => (
                            <li key={i} className="flex items-start gap-3">
                              <CheckCircle size={20} weight="fill" className="text-[#1B93A4] flex-shrink-0 mt-0.5" />
                              <span className="text-[#333333] text-sm">{feature}</span>
                            </li>
                          ))}
                        </ul>
                      </div>

                      <Link to="/contact">
                        <Button
                          className="bg-[#E74C3C] hover:bg-[#C0392B] text-white font-bold uppercase tracking-wide px-8 py-4 rounded-sm mt-4"
                          data-testid={`service-cta-${service.id}`}
                        >
                          Demander un Devis
                          <ArrowRight className="ml-2" size={20} weight="bold" />
                        </Button>
                      </Link>
                    </div>
                  </div>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Why Choose Us */}
      <section className="py-24 bg-[#333333]" data-testid="why-choose-us">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-3xl sm:text-4xl font-bold text-white">
              Pourquoi Choisir Qualidéco ?
            </h2>
          </motion.div>

          <div className="grid md:grid-cols-3 gap-8">
            {[
              {
                title: "Expertise Certifiée",
                description: "Certification Qualibat garantissant notre savoir-faire et la qualité de nos prestations.",
              },
              {
                title: "Devis Gratuit",
                description: "Étude personnalisée de votre projet avec un devis détaillé et transparent, sans engagement.",
              },
              {
                title: "Garantie Décennale",
                description: "Tous nos travaux sont couverts par notre assurance décennale pour votre tranquillité.",
              },
            ].map((item, index) => (
              <motion.div
                key={item.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="bg-white/5 border border-white/10 p-8 rounded-sm"
              >
                <div className="w-12 h-12 bg-[#1B93A4] rounded-sm flex items-center justify-center mb-4">
                  <CheckCircle size={24} weight="fill" className="text-white" />
                </div>
                <h3 className="text-xl font-bold text-white mb-3">{item.title}</h3>
                <p className="text-gray-400">{item.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-24 bg-[#F8F9FA]" data-testid="services-cta">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="text-3xl sm:text-4xl font-bold text-[#333333] mb-6">
              Un Projet en Tête ?
            </h2>
            <p className="text-[#64748B] text-lg mb-8">
              Contactez-nous pour discuter de votre projet. Notre équipe vous accompagne 
              de l'étude à la réalisation de vos travaux.
            </p>
            <Link to="/contact">
              <Button
                className="bg-[#E74C3C] hover:bg-[#C0392B] text-white font-bold uppercase tracking-wide px-10 py-6 rounded-sm text-base"
                data-testid="services-final-cta"
              >
                Contactez-nous
                <ArrowRight className="ml-2" size={20} weight="bold" />
              </Button>
            </Link>
          </motion.div>
        </div>
      </section>
    </div>
  );
}
